import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { NotificationsLogic } from '@framework/common-web/schema/notifications/NotificationLogic.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { generatePath } from '@framework/ui/core/router/TypedRoute.tsx';
import { useMemo } from 'react';
import useRegisterRecordsMenuItems from '#build-flags/routes/register-records/menu/useRegisterRecordsMenuItems.tsx';
import { AddressBookMenuItems } from '#features/apps/frontoffice-web/src/routes/address-book/menu/AddressBookMenuItems.ts';
import { type BaseMenuItemsProps, useAdditionalFrontMenuItems } from '#theme';
import { type RoutePath } from '../RoutePaths.ts';
import { type MenuItemsProps, MenuItemsType } from './MainMenu.tsx';
import { useDossierMenuItemVisibility } from './useDossierMenuItemVisibility.ts';

const parseThemeFrontAdditionalMenuItems = (
  items: BaseMenuItemsProps[],
): MenuItemsProps[] => {
  return items.map(item => ({
    ...item,
    type: MenuItemsType.LEAF,
    // `to` non è tipizzato nel tema perché per definizione non abbiamo accesso alle RoutePath,
    // che sono presenti solo in frontoffice.
    to: generatePath(item.to as RoutePath, {}),
  }));
};

export function useMainMenuItem(): MenuItemsProps[] {
  const currentUser = useCurrentUser();
  const themeAdditional = useAdditionalFrontMenuItems();
  const dossierMenuVisibility = useDossierMenuItemVisibility(currentUser);

  const registerRecordsMenuItems = process.env.BUILD_FLAGS_REGISTER_RECORDS
    ? // biome-ignore lint/correctness/useHookAtTopLevel: Hook dietro Build Flag
      useRegisterRecordsMenuItems() // NOSONAR
    : [];

  const result = useMemo(() => {
    const result: MenuItemsProps[] = [
      {
        menuKey: 'domande',
        type: MenuItemsType.LEAF,
        to: '/domande',
        children: t('menu.tutte-le-domande', 'Tutte le Domande'),
        title: t('menu.tutte-le-domande.sr-title', 'Vai a Tutte le Domande'),
      },
    ];

    if (currentUser) {
      result.push({
        menuKey: 'richieste',
        type: MenuItemsType.LEAF,
        to: generatePath('/richieste', {}),
        children: t('menu.le-mie-richieste', 'Le mie Richieste'),
        title: t('menu.le-mie-richieste.sr-title', 'Vai a Le mie Richieste'),
      });
    }

    // Se il menù è abilitato
    if (dossierMenuVisibility.enabled) {
      // Se non è in caricamento oppure è visibile
      if (dossierMenuVisibility.isLoading || dossierMenuVisibility.visible) {
        result.push({
          menuKey: 'pratiche',
          type: MenuItemsType.LEAF,
          to: generatePath('/pratiche', {}),
          children: t('menu.le-mie-pratiche', 'Le mie Pratiche'),
          title: t('menu.le-mie-pratiche.sr-title', 'Vai a Le mie Pratiche'),
          // Se è in caricamento, mostra il caricamento
          loading: dossierMenuVisibility.isLoading,
        });
      }
    }

    if (currentUser && NotificationsLogic.showListPage()) {
      result.push({
        menuKey: 'notifiche',
        type: MenuItemsType.LEAF,
        to: generatePath('/notifiche', {}),
        children: t('menu.notifiche', 'Notifiche'),
        title: t('menu.notifiche.sr-title', 'Vai a Notifiche'),
      });
    }

    // Aggiungiamo il menù Rubrica
    if (currentUser) {
      result.push(...AddressBookMenuItems);
    }

    const additional = parseThemeFrontAdditionalMenuItems(themeAdditional);

    return [...result, ...additional, ...registerRecordsMenuItems];
  }, [
    currentUser,
    dossierMenuVisibility.enabled,
    dossierMenuVisibility.isLoading,
    dossierMenuVisibility.visible,
    themeAdditional,
    registerRecordsMenuItems,
  ]);

  return result;
}
